/**
 * PurchasePage — Master product selector.
 *
 * Fetches the product catalog from the API, merges with local display
 * metadata, and renders product cards. Each card links to /purchase/:slug.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Camera,
    Link as LinkIcon,
    Loader2,
    AlertCircle,
    ArrowRight,
    ShoppingBag,
    Check,
} from 'lucide-react';
import * as api from '../lib/api';
import { enrichProducts } from '../config/products';

const ICON_MAP = {
    Camera,
    Link: LinkIcon,
};

const ACCENT_CLASSES = {
    blue: {
        badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
        border: 'hover:border-indigo-400 dark:hover:border-indigo-500',
        cta: 'text-indigo-600 dark:text-indigo-400',
        ring: 'group-hover:ring-indigo-200 dark:group-hover:ring-indigo-800',
    },
    emerald: {
        badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
        border: 'hover:border-emerald-400 dark:hover:border-emerald-500',
        cta: 'text-emerald-600 dark:text-emerald-400',
        ring: 'group-hover:ring-emerald-200 dark:group-hover:ring-emerald-800',
    },
};

export function PurchasePage() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const data = await api.fetchProducts();
                if (!cancelled) {
                    setProducts(enrichProducts(data));
                    setIsLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || 'Failed to load products.');
                    setIsLoading(false);
                }
            }
        }
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className="animate-fade-in">
            {/* Header */}
            <div className="text-center mb-8">
                <ShoppingBag className="w-10 h-10 mx-auto text-indigo-600 dark:text-indigo-400 mb-3" />
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                    Get a License
                </h2>
                <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm mx-auto">
                    Choose a product to unlock Pro features. One-time purchase — no subscription.
                </p>
            </div>

            {/* Loading state */}
            {isLoading && (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
                </div>
            )}

            {/* Error state */}
            {error && (
                <div
                    className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 px-4 py-3 rounded-lg"
                    role="alert"
                >
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* Product cards */}
            {!isLoading && !error && (
                <div className="space-y-4">
                    {products.map((product) => {
                        const Icon = ICON_MAP[product.icon] || ShoppingBag;
                        const colors = ACCENT_CLASSES[product.accent] || ACCENT_CLASSES.blue;

                        return (
                            <Link
                                key={product.slug}
                                to={`/purchase/${product.slug}`}
                                className={`group block bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl p-5 transition-all duration-200 cursor-pointer ${colors.border} ring-1 ring-transparent ${colors.ring}`}
                            >
                                <div className="flex items-start gap-4">
                                    {/* Icon */}
                                    <div className="shrink-0 w-11 h-11 rounded-lg bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                                        <Icon className="w-5 h-5 text-zinc-600 dark:text-zinc-300" />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2.5 mb-1">
                                            <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
                                                {product.name}
                                            </h3>
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${colors.badge}`}
                                            >
                                                {product.badgeLabel}
                                                {!product.isFree && (
                                                    <span className="ml-0.5 font-normal opacity-80">
                                                        {' '}
                                                        one-time
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-3">
                                            {product.tagline}
                                        </p>

                                        {/* Feature highlights */}
                                        <ul className="space-y-1">
                                            {product.highlights.slice(0, 3).map((h) => (
                                                <li
                                                    key={h}
                                                    className="flex items-center gap-2 text-xs text-zinc-600 dark:text-zinc-400"
                                                >
                                                    <Check className="w-3.5 h-3.5 shrink-0 text-emerald-500" />
                                                    {h}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Arrow */}
                                    <ArrowRight className="w-5 h-5 shrink-0 text-zinc-400 group-hover:text-zinc-600 dark:group-hover:text-zinc-200 transition-colors mt-1" />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Existing license link */}
            <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-800 text-center">
                <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Already have a license?{' '}
                    <Link
                        to="/lookup"
                        className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                    >
                        Look up your license
                    </Link>
                </p>
            </div>
        </div>
    );
}
